
import { useEffect, useState } from "react";
import {
  Edit2,
  Eye,
  Package,
  X,
  User,
  MapPin,
  Phone,
  Mail,
  Truck,
  CheckCircle,
  Clock,
  XCircle,
  ShoppingBag,
  RefreshCw,
} from "lucide-react";
import { orderService } from "../../services/orderService";

const STATUS_OPTIONS = [
  "En attente",
  "Confirmée",
  "Expédiée",
  "Livrée",
  "Annulée",
];

const FALLBACK_IMAGE =
  "https://placehold.co/100x100/f5f3f0/6b6b6b?text=Produit";

const STATUS_STYLES = {
  "En attente": "bg-amber-100 text-amber-800 ring-amber-200",
  "Confirmée": "bg-sky-100 text-sky-800 ring-sky-200",
  "Expédiée": "bg-violet-100 text-violet-800 ring-violet-200",
  "Livrée": "bg-emerald-100 text-emerald-800 ring-emerald-200",
  "Annulée": "bg-red-100 text-red-800 ring-red-200",
};

const STATUS_ICONS = {
  "En attente": Clock,
  "Confirmée": CheckCircle,
  "Expédiée": Truck,
  "Livrée": CheckCircle,
  "Annulée": XCircle,
};

function formatPrice(value) {
  const amount = Number(value ?? 0);

  return `${(Number.isFinite(amount) ? amount : 0).toLocaleString(
    "fr-MA",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )} MAD`;
}

function getStatusColor(status) {
  return (
    STATUS_STYLES[status] ||
    "bg-stone-100 text-stone-700 ring-stone-200"
  );
}

function getCustomerName(order) {
  const firstName = order.shipping?.firstName || "";
  const lastName = order.shipping?.lastName || "";

  return `${firstName} ${lastName}`.trim() || "Client non renseigné";
}

function getProductImage(item) {
  return item.image || item.mainImage || item.images?.[0] || FALLBACK_IMAGE;
}

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editStatus, setEditStatus] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadOrders = async () => {
      setLoading(true);
      setError("");

      try {
        const data = await orderService.getAll();

        if (!cancelled) {
          setOrders(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err.message || "Impossible de charger les commandes."
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadOrders();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleEditStatus = (order) => {
    setEditingId(order.id);
    setEditStatus(order.status || "En attente");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditStatus("");
  };

  const handleSaveStatus = async (orderId) => {
    if (!editStatus || savingId) return;

    setSavingId(orderId);
    setError("");

    try {
      await orderService.updateStatus(orderId, editStatus);

      setOrders((previousOrders) =>
        previousOrders.map((order) =>
          order.id === orderId
            ? { ...order, status: editStatus }
            : order
        )
      );

      setSelectedOrder((previousOrder) =>
        previousOrder?.id === orderId
          ? { ...previousOrder, status: editStatus }
          : previousOrder
      );

      setEditingId(null);
      setEditStatus("");
    } catch (err) {
      setError(
        `Impossible de modifier le statut : ${
          err.message || "Erreur inconnue"
        }`
      );
    } finally {
      setSavingId(null);
    }
  };

  const openOrderDetails = (order) => {
    setSelectedOrder(order);
  };

  const closeOrderDetails = () => {
    setSelectedOrder(null);
  };

  useEffect(() => {
    if (!selectedOrder) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeOrderDetails();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedOrder]);

  const totalOrders = orders.length;
  const pendingOrders = orders.filter(
    (order) => order.status === "En attente"
  ).length;
  const deliveredOrders = orders.filter(
    (order) => order.status === "Livrée"
  ).length;

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-stone-50">
        <div className="flex flex-col items-center gap-4">
          <div
            className="h-10 w-10 animate-spin rounded-full border-4 border-stone-200 border-t-[#c9a961]"
            role="status"
            aria-label="Chargement des commandes"
          />
          <p className="text-sm text-stone-500">
            Chargement des commandes...
          </p>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-stone-50 px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">

        {/* EN-TÊTE */}
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-[2px] text-[#c9a961]">
              Administration
            </p>

            <h1 className="flex items-center gap-3 text-2xl font-bold tracking-tight text-stone-800 sm:text-3xl">
              <Package className="text-[#c9a961]" size={30} />
              Gestion des commandes
            </h1>

            <p className="mt-2 text-sm text-stone-500">
              Consultez les commandes et gérez leur statut.
            </p>
          </div>

          <div className="inline-flex items-center gap-2 self-start rounded-full border border-stone-200 bg-white px-4 py-2 text-sm text-stone-600 shadow-sm">
            <ShoppingBag size={16} className="text-[#c9a961]" />
            {totalOrders} commande{totalOrders > 1 ? "s" : ""}
          </div>
        </div>

        {/* ERREUR */}
        {error && (
          <div
            role="alert"
            className="mb-6 flex items-start justify-between gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
          >
            <p>{error}</p>

            <button
              type="button"
              onClick={() => setError("")}
              aria-label="Fermer le message d'erreur"
              className="shrink-0 rounded p-1 hover:bg-red-100"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* INDICATEURS */}
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <p className="text-sm text-stone-500">Total commandes</p>
              <ShoppingBag size={20} className="text-[#c9a961]" />
            </div>

            <p className="mt-3 text-3xl font-bold text-stone-800">
              {totalOrders}
            </p>
          </div>

          <div className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <p className="text-sm text-stone-500">En attente</p>
              <Clock size={20} className="text-amber-500" />
            </div>

            <p className="mt-3 text-3xl font-bold text-stone-800">
              {pendingOrders}
            </p>
          </div>

          <div className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <p className="text-sm text-stone-500">Livrées</p>
              <CheckCircle size={20} className="text-emerald-600" />
            </div>

            <p className="mt-3 text-3xl font-bold text-stone-800">
              {deliveredOrders}
            </p>
          </div>
        </div>

        {/* TABLEAU */}
        <section className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-stone-100 px-5 py-4 sm:px-6">
            <h2 className="font-semibold text-stone-800">
              Liste des commandes
            </h2>

            <span className="text-xs text-stone-400">
              {orders.length} résultat{orders.length > 1 ? "s" : ""}
            </span>
          </div>

          {orders.length === 0 ? (
            <div className="flex flex-col items-center px-6 py-16 text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-stone-100">
                <Package size={26} className="text-stone-400" />
              </div>

              <h3 className="font-semibold text-stone-800">
                Aucune commande
              </h3>

              <p className="mt-2 text-sm text-stone-500">
                Les commandes apparaîtront ici lorsqu'elles seront créées.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[850px] border-collapse text-left">
                <thead>
                  <tr className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
                    <th className="px-5 py-4 font-semibold">Client</th>
                    <th className="px-5 py-4 font-semibold">Produits</th>
                    <th className="px-5 py-4 font-semibold">Articles</th>
                    <th className="px-5 py-4 font-semibold">Total</th>
                    <th className="px-5 py-4 font-semibold">Statut</th>
                    <th className="px-5 py-4 text-right font-semibold">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-stone-100">
                  {orders.map((order) => {
                    const items = Array.isArray(order.items)
                      ? order.items
                      : [];

                    const StatusIcon =
                      STATUS_ICONS[order.status] || Clock;

                    return (
                      <tr
                        key={order.id}
                        className="transition hover:bg-stone-50/70"
                      >
                        {/* CLIENT */}
                        <td className="px-5 py-5">
                          <p className="max-w-[190px] truncate text-sm font-semibold text-stone-800">
                            {getCustomerName(order)}
                          </p>

                          <p className="mt-1 max-w-[190px] truncate text-xs text-stone-500">
                            {order.shipping?.email || "Email non renseigné"}
                          </p>

                          <p className="mt-1 text-xs text-stone-400">
                            {order.shipping?.phone || ""}
                          </p>
                        </td>

                        {/* IMAGES */}
                        <td className="px-5 py-5">
                          <div className="flex items-center">
                            {items.slice(0, 3).map((item, index) => (
                              <img
                                key={`${item.id || item.name || "item"}-${index}`}
                                src={getProductImage(item)}
                                alt={item.name || "Produit"}
                                title={item.name || "Produit"}
                                onError={(event) => {
                                  event.currentTarget.onerror = null;
                                  event.currentTarget.src = FALLBACK_IMAGE;
                                }}
                                className={`h-10 w-10 rounded-lg border-2 border-white bg-stone-100 object-contain shadow-sm ${
                                  index > 0 ? "-ml-3" : ""
                                }`}
                              />
                            ))}

                            {items.length > 3 && (
                              <span className="-ml-2 flex h-9 min-w-9 items-center justify-center rounded-full border-2 border-white bg-stone-100 px-2 text-[10px] font-bold text-stone-600">
                                +{items.length - 3}
                              </span>
                            )}

                            {items.length === 0 && (
                              <span className="text-xs text-stone-400">
                                Aucun produit
                              </span>
                            )}
                          </div>
                        </td>

                        {/* QUANTITÉ */}
                        <td className="px-5 py-5">
                          <span className="inline-flex rounded-full bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-700">
                            {items.reduce(
                              (sum, item) =>
                                sum + Number(item.quantity || 1),
                              0
                            )}{" "}
                            article(s)
                          </span>
                        </td>

                        {/* TOTAL */}
                        <td className="px-5 py-5">
                          <span className="whitespace-nowrap text-sm font-bold text-stone-800">
                            {formatPrice(order.total)}
                          </span>
                        </td>

                        {/* STATUT */}
                        <td className="px-5 py-5">
                          {editingId === order.id ? (
                            <div className="flex min-w-[190px] flex-col gap-2">
                              <select
                                value={editStatus}
                                onChange={(event) =>
                                  setEditStatus(event.target.value)
                                }
                                aria-label="Nouveau statut"
                                className="w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-xs outline-none focus:border-[#c9a961] focus:ring-2 focus:ring-[#c9a961]/20"
                              >
                                {STATUS_OPTIONS.map((status) => (
                                  <option key={status} value={status}>
                                    {status}
                                  </option>
                                ))}
                              </select>

                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleSaveStatus(order.id)
                                  }
                                  disabled={savingId === order.id}
                                  className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  {savingId === order.id
                                    ? "Enregistrement..."
                                    : "Enregistrer"}
                                </button>

                                <button
                                  type="button"
                                  onClick={handleCancelEdit}
                                  disabled={savingId === order.id}
                                  className="rounded-md border border-stone-200 px-3 py-1.5 text-xs text-stone-600 transition hover:bg-stone-100"
                                >
                                  Annuler
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex min-w-[150px] items-center gap-2">
                              <span
                                className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1.5 text-[11px] font-semibold ring-1 ring-inset ${getStatusColor(
                                  order.status
                                )}`}
                              >
                                <StatusIcon size={13} />
                                {order.status || "Non défini"}
                              </span>

                              <button
                                type="button"
                                onClick={() => handleEditStatus(order)}
                                title="Modifier le statut"
                                aria-label={`Modifier le statut de la commande ${order.id}`}
                                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-stone-200 text-stone-500 transition hover:border-[#c9a961] hover:bg-[#c9a961]/10 hover:text-[#a88742]"
                              >
                                <Edit2 size={14} />
                              </button>
                            </div>
                          )}
                        </td>

                        {/* ACTIONS */}
                        <td className="px-5 py-5 text-right">
                          <button
                            type="button"
                            onClick={() => openOrderDetails(order)}
                            className="inline-flex items-center gap-2 rounded-lg border border-stone-200 px-3 py-2 text-xs font-semibold text-stone-600 transition hover:border-[#c9a961] hover:bg-[#c9a961]/5 hover:text-[#a88742]"
                          >
                            <Eye size={15} />
                            Détails
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {/* MODALE DÉTAILS */}
      {selectedOrder && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 backdrop-blur-sm sm:p-6"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeOrderDetails();
            }
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="order-modal-title"
            className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
          >
            {/* EN-TÊTE MODALE */}
            <div className="flex items-start justify-between gap-4 border-b border-stone-100 px-5 py-5 sm:px-7">
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-[1.8px] text-[#c9a961]">
                  Détails de commande
                </p>

                <h2
                  id="order-modal-title"
                  className="text-lg font-bold text-stone-800 sm:text-xl"
                >
                  Commande #
                  {String(selectedOrder.id || "").slice(0, 8)}
                </h2>
              </div>

              <button
                type="button"
                onClick={closeOrderDetails}
                aria-label="Fermer les détails"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-stone-100 text-stone-500 transition hover:bg-stone-200 hover:text-stone-800"
              >
                <X size={18} />
              </button>
            </div>

            {/* CONTENU MODALE */}
            <div className="overflow-y-auto px-5 py-6 sm:px-7">

              {/* INFORMATIONS CLIENT */}
              <div className="mb-6">
                <h3 className="mb-4 flex items-center gap-2 text-sm font-bold text-stone-800">
                  <User size={17} className="text-[#c9a961]" />
                  Informations client
                </h3>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <InfoCard
                    icon={<User size={15} />}
                    label="Nom complet"
                    value={getCustomerName(selectedOrder)}
                  />

                  <InfoCard
                    icon={<Mail size={15} />}
                    label="Email"
                    value={selectedOrder.shipping?.email}
                  />

                  <InfoCard
                    icon={<Phone size={15} />}
                    label="Téléphone"
                    value={selectedOrder.shipping?.phone}
                  />

                  <InfoCard
                    icon={<MapPin size={15} />}
                    label="Adresse"
                    value={selectedOrder.shipping?.address}
                  />

                  <InfoCard
                    icon={<MapPin size={15} />}
                    label="Ville"
                    value={selectedOrder.shipping?.city}
                  />

                  <InfoCard
                    icon={<MapPin size={15} />}
                    label="Code postal"
                    value={selectedOrder.shipping?.zipCode}
                  />
                </div>
              </div>

              {/* PRODUITS COMMANDÉS */}
              <div className="mb-6 border-t border-stone-100 pt-6">
                <h3 className="mb-4 flex items-center gap-2 text-sm font-bold text-stone-800">
                  <ShoppingBag size={17} className="text-[#c9a961]" />
                  Produits commandés
                </h3>

                <div className="space-y-3">
                  {(selectedOrder.items || []).map((item, index) => {
                    const quantity = Number(item.quantity || 1);
                    const price = Number(item.price || 0);

                    return (
                      <div
                        key={`${item.id || item.name || "item"}-${index}`}
                        className="flex items-center gap-3 rounded-xl border border-stone-100 p-3 sm:gap-4"
                      >
                        <img
                          src={getProductImage(item)}
                          alt={item.name || "Produit"}
                          onError={(event) => {
                            event.currentTarget.onerror = null;
                            event.currentTarget.src = FALLBACK_IMAGE;
                          }}
                          className="h-16 w-16 shrink-0 rounded-lg bg-stone-50 object-contain p-1"
                        />

                        <div className="min-w-0 flex-1">
                          <p className="line-clamp-2 text-sm font-semibold text-stone-800">
                            {item.name || "Produit"}
                          </p>

                          <p className="mt-1 text-xs text-stone-500">
                            {quantity} × {formatPrice(price)}
                          </p>
                        </div>

                        <p className="shrink-0 text-right text-xs font-bold text-stone-800 sm:text-sm">
                          {formatPrice(price * quantity)}
                        </p>
                      </div>
                    );
                  })}

                  {(!selectedOrder.items ||
                    selectedOrder.items.length === 0) && (
                    <p className="rounded-lg bg-stone-50 p-4 text-sm text-stone-500">
                      Aucun produit renseigné dans cette commande.
                    </p>
                  )}
                </div>
              </div>

              {/* RÉCAPITULATIF */}
              <div className="rounded-xl bg-stone-50 p-4 sm:p-5">
                <h3 className="mb-4 text-sm font-bold text-stone-800">
                  Récapitulatif
                </h3>

                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between gap-4 text-stone-600">
                    <span>Sous-total</span>
                    <span>{formatPrice(selectedOrder.total)}</span>
                  </div>

                  <div className="flex items-center justify-between gap-4 text-stone-600">
                    <span>Livraison</span>
                    <span>Gratuite</span>
                  </div>

                  <div className="flex items-center justify-between gap-4 border-t border-stone-200 pt-3 font-bold text-stone-800">
                    <span>Total</span>
                    <span className="font-serif text-lg text-[#a88742]">
                      {formatPrice(selectedOrder.total)}
                    </span>
                  </div>
                </div>
              </div>

              {/* STATUT ET PAIEMENT */}
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="rounded-xl border border-stone-200 p-4">
                  <p className="mb-2 text-xs text-stone-500">
                    Statut de commande
                  </p>

                  <span
                    className={`inline-flex rounded-full px-3 py-1.5 text-xs font-semibold ring-1 ring-inset ${getStatusColor(
                      selectedOrder.status
                    )}`}
                  >
                    {selectedOrder.status || "Non défini"}
                  </span>
                </div>

                <div className="rounded-xl border border-stone-200 p-4">
                  <p className="mb-2 text-xs text-stone-500">
                    Mode de paiement
                  </p>

                  <p className="text-sm font-semibold text-stone-800">
                    {selectedOrder.payment || "Non renseigné"}
                  </p>
                </div>
              </div>
            </div>

            {/* PIED DE MODALE */}
            <div className="flex justify-end border-t border-stone-100 px-5 py-4 sm:px-7">
              <button
                type="button"
                onClick={closeOrderDetails}
                className="rounded-lg bg-stone-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-stone-700"
              >
                Fermer
              </button>
            </div>
          </section>
        </div>
      )}
    </main>
  );
}

function InfoCard({ icon, label, value }) {
  return (
    <div className="flex min-w-0 items-start gap-3 rounded-xl border border-stone-100 p-3">
      <span className="mt-0.5 shrink-0 text-[#c9a961]">{icon}</span>

      <div className="min-w-0">
        <p className="mb-1 text-[11px] text-stone-500">{label}</p>
        <p className="break-words text-sm font-medium text-stone-800">
          {value || "Non renseigné"}
        </p>
      </div>
    </div>
  );
}
